import { ProductDetailInfo } from "./ProductDetailInfo";
import { AddToFavoritesButton } from "./AddToFavoritesButton";
import React, { useEffect } from "react";
import { ProductDetailHeader } from "./ProductDetailHeader";
import { PendingProductButton } from "./PendingProductButton";
import { DealSelectorBis } from "./DealSelectorBis";
import ProductPurchase from "./ProductPurchase";
import { NumberInputUI } from "../../ui/components/NumberInputUI";
import { RoutePathBreadcrumb } from "../../ui";
import { findItemAvailability, generateItemTags } from "../../common/CatalogService";
import { useProductSelection } from "../application/useProductSelection";
import { AddToCartButton } from "./AddToCartButton";
import { GtmService } from "../../infrastructure/analytics/GtmService";
import { OfertasCombinables } from "./OfertasCombinables";
import { StockBypassNotice } from "./StockBypassNotice";
import { useCartContext } from "../../cart/context/CartContext";
import { useCartPromotions } from "../../cart/hooks/useCartPromotions";
import { useUserContext } from "../../user/infrastructure/UserContext";
import { spanishDateFormatter } from "../../ui/components/SpanishDateFormatter";

/**
 * @param {{
 *     product,
 *     onProductSelection
 * }} props
 * @constructor
 */
export const ProductDetail = ({ product }) => {
    const user = useUserContext();
    const {
        productSelection,
        dealSelection,
        setDealSelection,
        setQuantity
    } = useProductSelection(product, 1);

    const [hasActiveCombinableSelection, setHasActiveCombinableSelection] = React.useState(false);

    const { promotions } = useCartPromotions();

    // Only show combinable promos where this specific product is in the group
    const productPromos = React.useMemo(() => {
        const currentCode = product.code ?? product.codigoArticulo ?? '';
        return (promotions ?? []).filter(p => {
            if (p.scope !== 'PRODUCT' || !p.scopeValues) return false;
            const codes = p.scopeValues.split(',').map(s => s.trim());
            return codes.includes(currentCode);
        });
    }, [promotions, product.code, product.codigoArticulo]);

    // Non-PRODUCT scoped promos (GLOBAL, CATEGORY, BRAND, etc.) that the backend
    // already pre-filtered for this user — show them as a simple discount badge.
    const globalPromos = React.useMemo(() => {
        const currentCode = product.code ?? product.codigoArticulo ?? '';
        const currentCategoryId = product.subfamilyId ? String(product.subfamilyId) : null;
        const currentBrandId = product.manufacturerId ? String(product.manufacturerId) : null;
        return (promotions ?? []).filter(p => {
            if (p.scope === 'PRODUCT') return false; // handled by productPromos / OfertasCombinables
            if (p.scope === 'GLOBAL') return true;
            if (p.scope === 'SKU_LIST' && p.scopeValues) {
                const codes = p.scopeValues.split(',').map(s => s.trim());
                return codes.includes(currentCode);
            }
            if (p.scope === 'CATEGORY' && currentCategoryId && p.scopeValues) {
                const ids = p.scopeValues.split(',').map(s => s.trim());
                return ids.includes(currentCategoryId);
            }
            if (p.scope === 'BRAND' && currentBrandId && p.scopeValues) {
                const ids = p.scopeValues.split(',').map(s => s.trim());
                return ids.includes(currentBrandId);
            }
            if (p.scope === 'CUSTOMER' || p.scope === 'CUSTOMER_GROUP') return true; // backend already filtered by user
            return false;
        });
    }, [promotions, product]);

    const { cart } = useCartContext();
    const baseSellingPrice = productSelection.price?.sellingPrice ?? product.price?.bestPrice;

    // If an OVERRIDE_FULL promo is active for this product, its fixedPrice IS the selling price.
    // Works for PRODUCT scope and also GLOBAL scope.
    const overrideFullPrice = React.useMemo(() => {
        const currentCode = product.code ?? product.codigoArticulo ?? '';
        const overrideFull = (promotions ?? []).find(p =>
            (p.stackingType || p.stacking_type) === 'OVERRIDE_FULL' &&
            p.fixedPrice != null &&
            (
                p.scope === 'GLOBAL' ||
                (p.scope === 'PRODUCT' && p.scopeValues?.split(',').map(s => s.trim()).includes(currentCode)) ||
                (p.scope === 'SKU_LIST' && p.scopeValues?.split(',').map(s => s.trim()).includes(currentCode))
            )
        );
        return overrideFull?.fixedPrice ?? null;
    }, [promotions, product.code, product.codigoArticulo]);

    const effectiveUnitPrice = overrideFullPrice ?? baseSellingPrice;

    useEffect(() => {
        GtmService.logViewItem(product);
    }, [product]);

    const hasStock = React.useMemo(
        () => product.availableUnits > 0 || product.onDemand,
        [product.availableUnits, product.onDemand]
    );

    const header = React.useMemo(() => ({
        id: product.id,
        code: product.code,
        name: product.name,
        provider: product.manufacturer,
        price: !product.price ? null : {
            ...product.price,
            // If OVERRIDE_FULL is active, show fixed price prominently and hide ERP discount line
            ...(overrideFullPrice != null ? { bestPrice: overrideFullPrice, discountLine: null } : {})
        },
        availability: findItemAvailability(product, productSelection.quantity),
        hasStock
    }), [product, productSelection.quantity, hasStock, overrideFullPrice]);

    const info = React.useMemo(() => ({
        imageUrl: product.imageUrl,
        secondaryImages: product.secondaryImages,
        description: product.description,
        cimavetFileUrl: product.cimavetFileUrl,
        needsPrescription: product.needsPrescription,
        needsRefrigeration: product.needsRefrigeration,
        isPsicotropic: product.isPsicotropic,
        isNarcotic: product.isNarcotic,
        isAntibiotic: product.isAntibiotic,
        aiSummary: product.aiSummary,
        // Etiquetas (Combinable, Ofertas, Nuevo...) sobre la imagen, como en las tarjetas del catalogo
        tags: generateItemTags(product)
    }), [product]);

    const dealData = React.useMemo(() => ({
        offers: product.offers,
        lots: product.lots,
        promos: product.promos,
        hasDealsToChoose: product.hasDealsToChoose
    }), [product]);

    const recap = React.useMemo(() => {
        if (!productSelection?.price || !productSelection?.discount) {
            return;
        }
        const basePrice = productSelection.price.basePrice;
        const qty = productSelection.quantity;
        const promoUnitPrice = effectiveUnitPrice ?? productSelection.price.sellingPrice;
        const hasPromo = promoUnitPrice < productSelection.price.sellingPrice;

        // When OVERRIDE_FULL is active, the ERP % discount is irrelevant.
        // Show the real effective % from catalog price to the fixed price instead.
        const effectiveDiscountPercentage = overrideFullPrice != null && basePrice > 0
            ? ((basePrice - overrideFullPrice) / basePrice) * 100
            : productSelection.discount.percentage;

        return ({
            price: {
                value: basePrice * qty,
                bestPrice: promoUnitPrice * qty
            },
            discount: {
                value: productSelection.discount.value + (productSelection.price.sellingPrice - promoUnitPrice) * qty,
                percentage: effectiveDiscountPercentage,
                line: hasPromo
                    ? `Precio especial: ${promoUnitPrice.toFixed(2)}€/ud`
                    : productSelection.discount.text
            }
        })
    }, [productSelection, effectiveUnitPrice, overrideFullPrice]);

    // Los productos con bypass de stock (bajo pedido) no pasan por pendientes:
    // se piden directamente aunque no haya unidades.
    const showPendingButton = React.useMemo(() =>
        (product.isPending || !header.hasStock) && !product.stockBypass,
        [product.isPending, header.hasStock, product.stockBypass]
    );

    return (
        <section className="row">
            <div className="col-12 mb-2">
                <RoutePathBreadcrumb
                    customBones={{
                        [product.code]: {
                            name: product.name,
                            path: `/articulos/${product.code}`,
                        }
                    }}
                />
            </div>
            <div className="col-12 col-lg-6 order-lg-last d-flex flex-column gap-4">
                <ProductDetailHeader header={header}>
                    {showPendingButton &&
                        <ProductDetailHeader.Pending>
                            <PendingProductButton
                                productCode={product.code}
                                defaultValue={product.isPending}
                                quantity={productSelection.quantity}
                                showText={true}
                            />
                        </ProductDetailHeader.Pending>
                    }
                </ProductDetailHeader>

                {/* Con stock el aviso de bajo pedido confunde (BC marca bajo pedido a productos
                    que sí tienen unidades): solo se enseña cuando de verdad no hay stock */}
                {!(product.availableUnits > 0) &&
                    <StockBypassNotice product={product} variant="pill" pillColor="primary" className="align-self-start" />
                }

                {/* Pack de venta de BC: los complementos se añaden sin coste al carrito
                    automáticamente junto al padre (los genera el backend) */}
                {product.packComponents?.length > 0 && (
                    <div className="border rounded p-3 mb-2 bg-white">
                        <h6 className="text-success mb-2">Este producto incluye</h6>
                        {product.packComponents.map(component => (
                            <div key={component.code} className="d-flex align-items-center gap-2 mb-1">
                                {!!component.imageUrl && (
                                    <img src={component.imageUrl} alt={component.name}
                                        style={{ width: '40px', height: '40px', objectFit: 'contain' }} />
                                )}
                                <div className="small">
                                    <div>{component.name}</div>
                                    <div className="text-success fw-bold">
                                        {component.quantityPerUnit} ud{component.quantityPerUnit !== 1 ? 's' : ''}. incluida{component.quantityPerUnit !== 1 ? 's' : ''}
                                        por cada unidad
                                    </div>
                                </div>
                            </div>
                        ))}
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                            Se añade automáticamente al carrito junto al producto, sin coste adicional
                        </div>
                    </div>
                )}

                <OfertasCombinables
                    promotions={productPromos}
                    catalogProducts={[product]}
                    sellingPricePerUnit={product.price?.sellingPrice ?? product.price?.bestPrice}
                    onActiveItemsChange={setHasActiveCombinableSelection}
                />

                {/* Global/group promotions — shown as informational badges */}
                {globalPromos.length > 0 && (
                    <div className="border rounded p-3 mb-2 bg-white">
                        <h6 className="text-success mb-2">🏷️ Oferta especial para tu cuenta</h6>
                        {globalPromos.map(p => (
                            <div key={p.id} className="small d-flex align-items-center gap-2 mb-1">
                                <span className="badge bg-success">{p.discountPercentage ? `-${p.discountPercentage}%` : p.discountTotalAmount ? `-${p.discountTotalAmount}€` : p.fixedPrice ? `${p.fixedPrice.toFixed(2)}€` : 'Dto.'}</span>
                                <span>{p.description}</span>
                                {p.minAmount > 0 ? (
                                    <span className="text-muted">(mín. {p.minAmount} €)</span>
                                ) : p.minQuantity > 0 ? (
                                    <span className="text-muted">(mín. {p.minQuantity} uds)</span>
                                ) : null}
                            </div>
                        ))}
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>Se aplica automáticamente al añadir al carrito</div>
                    </div>
                )}

                <DealSelectorBis dealData={dealData} selection={dealSelection} onSelectionChange={setDealSelection} />

                {user?.isComercial && !!product.description2 && (
                    <div className="border rounded p-3 bg-light">
                        <small className="fw-bold text-secondary d-block mb-2">Descripción 2 (solo visible para comerciales)</small>
                        <div style={{ fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>{product.description2}</div>
                    </div>
                )}

                {/* Sin stock disponible los lotes confunden: puede haber lotes con unidades
                    físicas pero todo comprometido (disponible neto 0 o negativo) */}
                {user?.isComercial && product.availableUnits > 0 && product.lotInfo?.length > 0 && (
                    <div className="border rounded p-3 bg-light">
                        <small className="fw-bold text-secondary d-block mb-2">Lotes disponibles (solo visible para comerciales)</small>
                        <table className="table table-sm table-borderless mb-0">
                            <thead>
                                <tr className="text-muted" style={{ fontSize: '0.75rem' }}>
                                    <th>Lote</th>
                                    <th>Caducidad</th>
                                    <th>Stock</th>
                                </tr>
                            </thead>
                            <tbody>
                                {product.lotInfo.map(lot => (
                                    <tr key={lot.code} style={{ fontSize: '0.8rem' }}>
                                        <td>{lot.code}</td>
                                        <td>{spanishDateFormatter(lot.expirationDate)}</td>
                                        <td>{lot.stock} uds</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <ProductPurchase recap={hasActiveCombinableSelection ? {
                    price: { value: hasActiveCombinableSelection.totalRawPrice, bestPrice: hasActiveCombinableSelection.totalFinalPrice },
                    discount: { value: hasActiveCombinableSelection.totalSavings, percentage: hasActiveCombinableSelection.activeTier?.totalAccumulatedPercentage ?? hasActiveCombinableSelection.exactDiscountPercentage ?? 0, line: '' }
                } : recap}>
                    <ProductPurchase.PurchaseControls>
                        {hasActiveCombinableSelection ? (
                            <div className="d-flex gap-4 align-items-center justify-content-center justify-content-xl-end">
                                <div className="small text-end me-3 d-none d-md-block">
                                    <strong>Seleccionado: {hasActiveCombinableSelection.totalQty} uds</strong>
                                    {!hasActiveCombinableSelection.activeTier && hasActiveCombinableSelection.nextTier && (
                                        <div className="text-muted" style={{ fontSize: '0.75rem', lineHeight: 1 }}>
                                            Faltan {hasActiveCombinableSelection.missingForNext} para {(hasActiveCombinableSelection.nextTier.discountTotalAmount ?? 0) > 0
                                                ? `${hasActiveCombinableSelection.nextTier.discountTotalAmount}€ dto.`
                                                : (hasActiveCombinableSelection.nextTier.discountAmount ?? 0) > 0
                                                    ? `${hasActiveCombinableSelection.nextTier.discountAmount}€/ud`
                                                    : `${hasActiveCombinableSelection.nextTier.discountPercentage}%`}
                                        </div>
                                    )}
                                </div>
                                <button
                                    className={`btn ${hasActiveCombinableSelection.applied ? 'btn-success' : 'btn-primary'}`}
                                    onClick={hasActiveCombinableSelection.handleApply}
                                    disabled={hasActiveCombinableSelection.totalQty === 0 || hasActiveCombinableSelection.applying}>
                                    {hasActiveCombinableSelection.applying ? '⏳ Añadiendo...' : hasActiveCombinableSelection.applied ? '✅ Añadido' : (
                                        <><i className="bi bi-cart3 me-1"></i> Agregar</>
                                    )}
                                </button>
                            </div>
                        ) : (
                            <div className="d-flex gap-4 align-items-center justify-content-center justify-content-xl-end">
                                <NumberInputUI data={{ value: productSelection.quantity }} actions={{ setValue: setQuantity }} />
                                <AddToCartButton productSelection={productSelection} hasStock={hasStock} product={product} />
                            </div>
                        )}
                    </ProductPurchase.PurchaseControls>
                </ProductPurchase>

                {hasActiveCombinableSelection?.applyError && (
                    <div className="alert alert-danger mt-2 py-1 small">{hasActiveCombinableSelection.applyError}</div>
                )}
            </div>
            <div className="col-12 col-lg-6">
                <ProductDetailInfo info={info}>
                    <ProductDetailInfo.AddToFavorites>
                        <AddToFavoritesButton data={{
                            productCode: product.code,
                            show: true,
                            isFavorite: product.isFavorite
                        }} />
                    </ProductDetailInfo.AddToFavorites>
                </ProductDetailInfo>
            </div>
        </section>
    );
}
